/**
 * useCurrencies Hook
 *
 * Fetches currencies and default currency from Firestore.
 * Uses cache so re-entering the Accounts page shows summary numbers immediately (no reload).
 * Handles currency rate calculations and workspace settings.
 */

import { useState, useEffect, useRef } from "react";
import type React from "react";
import { auth } from "../../../firebase";
import {
  collection,
  query,
  onSnapshot,
  doc,
  getDocs,
  Timestamp,
  DocumentSnapshot,
  FirestoreError,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { Currency, WorkspaceSettings, ValuesVisibilityMode } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";

const currenciesCache = new Map<string, { currencies: Currency[]; defaultCurrency: string; timestamp: number }>();
const CACHE_EXPIRY_MS = 5 * 60 * 1000;

/**
 * Hook to fetch currencies and workspace settings
 * @param workspaceId - Workspace ID
 * @returns Object with currencies array, default currency, visibility mode, and values visible state
 */
export function useCurrencies(workspaceId: string | undefined): {
  currencies: Currency[];
  defaultCurrency: string;
  valuesVisibilityMode: ValuesVisibilityMode;
  valuesVisible: boolean;
  setValuesVisible: React.Dispatch<React.SetStateAction<boolean>>;
} {
  const cached = workspaceId ? currenciesCache.get(workspaceId) : null;
  const hasValidCache = !!(cached && (Date.now() - cached.timestamp) < CACHE_EXPIRY_MS);

  const [currencies, setCurrencies] = useState<Currency[]>(hasValidCache && cached ? cached.currencies : []);
  const [defaultCurrency, setDefaultCurrency] = useState<string>(hasValidCache && cached ? cached.defaultCurrency : "USD");
  const [valuesVisibilityMode, setValuesVisibilityMode] = useState<ValuesVisibilityMode>("manual");
  const [valuesVisible, setValuesVisible] = useState<boolean>(true);
  const defaultCurrencyRef = useRef<string>(hasValidCache && cached ? cached.defaultCurrency : "USD");
  defaultCurrencyRef.current = defaultCurrency;

  // Fetch currencies and default currency
  useEffect(() => {
    // Allow loading in demo mode or when authenticated
    if (!workspaceId || (!auth.currentUser && !isDemoMode())) return;

    // Fetch default currency and visibility mode
    const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
    const unsubscribeSettings = onSnapshot(settingsRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data() as WorkspaceSettings;
        const newDefault = data.defaultCurrency || "USD";
        setDefaultCurrency(newDefault);
        defaultCurrencyRef.current = newDefault;
        const prev = currenciesCache.get(workspaceId!);
        currenciesCache.set(workspaceId!, { currencies: prev?.currencies ?? [], defaultCurrency: newDefault, timestamp: Date.now() });
        const visibilityMode = data.valuesVisibilityMode || "manual";
        setValuesVisibilityMode(visibilityMode);
        
        // Apply behavior based on mode
        if (visibilityMode === "always-closed") {
          // Always-closed mode: always start with hidden (ignore localStorage)
          setValuesVisible(false);
          // Don't use localStorage, always default to hidden
        } else if (visibilityMode === "always-visible") {
          // Always-visible mode: always visible regardless of user action
          setValuesVisible(true);
          // Clear localStorage if any
          localStorage.removeItem(`valuesVisible_${workspaceId}`);
        } else {
          // Manual mode: use localStorage to persist user preference, default to visible
          const savedState = localStorage.getItem(`valuesVisible_${workspaceId}`);
          setValuesVisible(savedState !== null ? savedState === "true" : true);
        }
      } else {
        // Default to manual mode
        setValuesVisibilityMode("manual");
        const savedState = localStorage.getItem(`valuesVisible_${workspaceId}`);
        setValuesVisible(savedState !== null ? savedState === "true" : true);
      }
    });

    // Fetch currencies
    const currenciesRef = collection(db, "workspaces", workspaceId, "currencies");
    const basicQuery = query(currenciesRef);
    
    const unsubscribeCurrencies = onSnapshot(
      basicQuery,
      (snapshot) => {
        const currenciesList: Currency[] = snapshot.docs
          .map((docSnap: DocumentSnapshot) => {
            const data = docSnap.data();
            if (!data) return null;
            
            const rates = (data.rates as Record<string, number | null>) || {};
            let currentRate: number | null;
            if (rates[defaultCurrency] !== undefined) {
              currentRate = rates[defaultCurrency];
            } else if (typeof data.rate === "number") {
              currentRate = data.rate;
            } else if (data.rate === null || data.rate === undefined) {
              currentRate = null;
            } else {
              const parsed = parseFloat(String(data.rate));
              currentRate = isNaN(parsed) ? null : parsed;
            }
            
            return {
              code: docSnap.id,
              name: (data.name as string) || "",
              symbol: (data.symbol as string) || "",
              rate: currentRate,
              rates: rates,
              decimalPlaces: data.decimalPlaces as number | undefined,
              updatedAt: data.updatedAt || data.createdAt || Timestamp.now(),
            } as Currency;
          })
          .filter((c): c is Currency => c !== null);
        
        setCurrencies(currenciesList);
        currenciesCache.set(workspaceId!, {
          currencies: currenciesList,
          defaultCurrency: defaultCurrencyRef.current,
          timestamp: Date.now(),
        });
      },
      (error: FirestoreError) => {
        console.error("Error fetching currencies:", error);
        getDocs(currenciesRef)
          .then((snapshot) => {
            const currenciesList: Currency[] = snapshot.docs
              .map((docSnap: DocumentSnapshot) => {
                const data = docSnap.data();
                if (!data) return null;
                const rates = (data.rates as Record<string, number | null>) || {};
                const currentRate = rates[defaultCurrency] ?? (typeof data.rate === "number" ? data.rate : null);
                return {
                  code: docSnap.id,
                  name: (data.name as string) || "",
                  symbol: (data.symbol as string) || "",
                  rate: currentRate,
                  rates: rates,
                  decimalPlaces: data.decimalPlaces as number | undefined,
                  updatedAt: data.updatedAt || data.createdAt || Timestamp.now(),
                } as Currency;
              })
              .filter((c): c is Currency => c !== null);
            setCurrencies(currenciesList);
            currenciesCache.set(workspaceId!, {
              currencies: currenciesList,
              defaultCurrency: defaultCurrencyRef.current,
              timestamp: Date.now(),
            });
          })
          .catch(() => {
            setCurrencies([]);
          });
      }
    );

    return () => {
      unsubscribeSettings();
      unsubscribeCurrencies();
    };
  }, [workspaceId, defaultCurrency]);

  return {
    currencies,
    defaultCurrency,
    valuesVisibilityMode,
    valuesVisible,
    setValuesVisible,
  };
}
